package app.nestbridge.app

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.pointerInput
import com.karawek.core.ui.haptics.LocalHapticsEnabled
import app.nestbridge.app.screen.AppScreen
import app.nestbridge.app.usecase.LogoutUseCase
import app.nestbridge.app.usecase.ObserveAuthSessionUseCase
import app.nestbridge.app.usecase.RenewSessionUseCase
import app.nestbridge.app.usecase.PasskeyEnrollmentPromptUseCase
import app.nestbridge.app.util.launchSafely
import com.karawek.core.auth.usecase.RegisterPasskeyUseCase
import com.karawek.core.ui.components.CoreConfirmDialog
import app.nestbridge.config.BuildKonfig
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.backstack.rememberSaveableBackStack
import com.slack.circuit.foundation.Circuit
import com.slack.circuit.foundation.CircuitCompositionLocals
import com.slack.circuit.foundation.NavigableCircuitContent
import com.slack.circuit.foundation.rememberCircuitNavigator
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.drop
import me.tatarka.inject.annotations.Inject
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlin.time.TimeSource

typealias App = @Composable () -> Unit

@Inject
@Composable
fun App(
    circuit: Circuit,
    observeAuthSessionUseCase: ObserveAuthSessionUseCase,
    logoutUseCase: LogoutUseCase,
    renewSessionUseCase: RenewSessionUseCase,
    registerPasskeyUseCase: RegisterPasskeyUseCase,
    passkeyEnrollmentPrompt: PasskeyEnrollmentPromptUseCase,
) {
    CircuitCompositionLocals(circuit) {
        val backStack = rememberSaveableBackStack(root = AppScreen)
        val navigator = rememberCircuitNavigator(backStack) { }

        // Auth gate: whenever the session is cleared (logout, expiry, inactivity,
        // or a 401 that invalidated it), reset to the root, which re-gates to
        // login. Lives here in the always-composed host so it fires no matter
        // which screen is on top. drop(1) skips the value captured on subscribe.
        LaunchedEffect(Unit) {
            observeAuthSessionUseCase()
                .drop(1)
                .distinctUntilChanged()
                .collect { session ->
                    if (session == null) {
                        navigator.resetRoot(AppScreen)
                    }
                }
        }

        // Inactivity auto-logout. While authenticated, if the user doesn't touch
        // the screen for `sessionInactivityMinutes`, sign out proactively so PHI
        // isn't left visible on an idle device — no waiting for the next request.
        val session by observeAuthSessionUseCase().collectAsState(initial = null)
        val isAuthenticated = session != null
        val inactivityMinutes = BuildKonfig.sessionInactivityMinutes
        val sessionMinutes = BuildKonfig.stytchSessionDurationMinutes
        var lastActivity by remember { mutableStateOf(TimeSource.Monotonic.markNow()) }

        // One-time post-login passkey enrollment prompt.
        val passkeyScope = rememberCoroutineScope()
        var showPasskeyPrompt by remember { mutableStateOf(false) }
        var passkeyBusy by remember { mutableStateOf(false) }
        LaunchedEffect(isAuthenticated) {
            if (isAuthenticated && passkeyEnrollmentPrompt.shouldPrompt()) {
                showPasskeyPrompt = true
            }
        }

        LaunchedEffect(isAuthenticated, inactivityMinutes, sessionMinutes) {
            if (!isAuthenticated || inactivityMinutes <= 0) return@LaunchedEffect
            val timeout = inactivityMinutes.minutes
            // Slide the session while the user is active: renew often enough that
            // its expiry stays ahead of the inactivity window — after ~half the
            // head-room between the session length and the idle window.
            val renewEvery = ((sessionMinutes - inactivityMinutes) / 2)
                .coerceAtLeast(1)
                .minutes
            lastActivity = TimeSource.Monotonic.markNow()
            var lastRenewal = TimeSource.Monotonic.markNow()
            while (true) {
                delay(15.seconds)
                if (lastActivity.elapsedNow() >= timeout) {
                    logoutUseCase()
                    break
                }
                if (lastRenewal.elapsedNow() >= renewEvery) {
                    renewSessionUseCase(sessionMinutes)
                    lastRenewal = TimeSource.Monotonic.markNow()
                }
            }
        }

        NestBridgeTheme {
            CompositionLocalProvider(
                LocalHapticsEnabled provides true,
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .pointerInput(Unit) {
                            // Observe every touch without consuming it, so any
                            // interaction resets the inactivity countdown.
                            awaitPointerEventScope {
                                while (true) {
                                    awaitPointerEvent(PointerEventPass.Initial)
                                    lastActivity = TimeSource.Monotonic.markNow()
                                }
                            }
                        },
                ) {
                    NavigableCircuitContent(
                        navigator = navigator,
                        backStack = backStack,
                    )

                    if (showPasskeyPrompt) {
                        CoreConfirmDialog(
                            title = "Set up a passkey?",
                            message = "Use Face ID, Touch ID, or your device lock to sign in " +
                                "faster next time — no codes to wait for.",
                            confirmText = "Set up",
                            onConfirm = {
                                if (!passkeyBusy) {
                                    passkeyBusy = true
                                    passkeyScope.launchSafely(
                                        onError = {
                                            passkeyBusy = false
                                            showPasskeyPrompt = false
                                        },
                                    ) {
                                        registerPasskeyUseCase()
                                        passkeyEnrollmentPrompt.markPrompted()
                                        passkeyBusy = false
                                        showPasskeyPrompt = false
                                    }
                                }
                            },
                            onDismiss = {
                                showPasskeyPrompt = false
                                passkeyScope.launchSafely { passkeyEnrollmentPrompt.markPrompted() }
                            },
                        )
                    }
                }
            }
        }
    }
}
